import { money } from '../../lib/format.js'
import type { RouterOutputs } from '../../lib/trpc.js'
import { Icon } from '../Icon.js'
import { dayLabel, everyLabel } from '../repeat/rule.js'
import { ServiceTracks, type Joined, type TrackGroup } from '../repeat/ServiceTracks.js'
import {
  nextOn,
  planByVisit,
  serviceName,
  siteTitle,
  visitKey,
  visitsOf,
  type AgreementGroup,
  type GroupBy,
} from './agreement-view.js'
import { GroupHeading, RowFlags, TickBox, tickedOf } from './review-parts.js'

export type AgreementPage = RouterOutputs['agreements']['byId']
type Row = AgreementPage['schedules'][number]

/** "Every 3 months · $200.00 · stopped", under a service's name. */
export function trackSub(
  row: Pick<Row, 'intervalCount' | 'intervalUnit' | 'stoppedAt'> & { priceIncTax: number | null },
): string {
  return [
    everyLabel(row).replace(/^./, (letter) => letter.toUpperCase()),
    row.priceIncTax ? money(row.priceIncTax) : null,
    row.stoppedAt ? 'stopped' : null,
  ]
    .filter(Boolean)
    .join(' · ')
}

/** What the grid and the table both hand each group and row. */
export type ReviewActions = {
  /** Ticking, for the bulk bar. Absent for somebody who cannot edit. */
  selected?: ReadonlySet<string>
  onSelect?: (scheduleIds: readonly string[], checked: boolean) => void
  onAddService?: (siteId: string) => void
  onAddSites?: (group: AgreementGroup) => void
  onAddSite?: () => void
  onEditSite?: (siteId: string) => void
  onEditService?: (row: Row) => void
  onRenameSite?: (siteId: string, name: string | null) => Promise<unknown>
  /** A visit with no job yet, clicked on the timeline. */
  onVisit?: (visit: { scheduleId: string; sequence: number }) => void
}

/** Which groups are open, and opening or folding one. Absent, every group is open. */
export type Folding = { isOpen: (key: string) => boolean; toggle: (key: string) => void }

/** A group's heading with the actions that suit how the page is grouped. */
export function headingFor(group: AgreementGroup, by: GroupBy, actions: ReviewActions, fold?: Folding) {
  const ids = group.rows.map((row) => row.id)
  const { selected, onSelect } = actions
  return (
    <GroupHeading
      group={group}
      by={by}
      open={fold ? fold.isOpen(group.key) : true}
      onFold={fold ? () => fold.toggle(group.key) : undefined}
      ticked={selected ? tickedOf(ids, selected) : undefined}
      onTick={onSelect ? (checked) => onSelect(ids, checked) : undefined}
      onRename={group.siteId && actions.onRenameSite ? (name) => actions.onRenameSite!(group.siteId!, name) : undefined}
      onEditSite={group.siteId && actions.onEditSite ? () => actions.onEditSite!(group.siteId!) : undefined}
      onAddService={group.siteId && actions.onAddService ? () => actions.onAddService!(group.siteId!) : undefined}
      onAddSites={group.serviceTypeId && actions.onAddSites ? () => actions.onAddSites!(group) : undefined}
    />
  )
}

/** A folded group's next visit: the soonest of its services', or why there is none. */
export function groupAside(rows: readonly Row[]) {
  const next = rows
    .filter((row) => !row.paused)
    .map(nextOn)
    .filter((on): on is string => on !== null)
    .sort()[0]
  if (next) return dayLabel(next, { year: false })
  return rows.every((row) => row.paused) ? <span className="font-normal text-ink-400">Paused</span> : '—'
}

/** The service and the site a row is, said once for every label on it. */
export function rowNames(row: Row, clientId: string) {
  const service = serviceName(row)
  const site = siteTitle(row.site, clientId).name
  return { service, site, both: `${service} at ${site}` }
}

/**
 * The agreement as a year on a page: each site and the services at it, or
 * each service and the sites that have it, and each visit of the term in
 * its month. Decision 96.
 *
 * Every dot is a slot the server computed with `repeatSlots`, the same one the
 * job card's timeline draws, so the grid and the tab cannot disagree about
 * when a visit falls. A dot with a job opens it; one without opens the visit.
 * Visits that are, or will be, one job are drawn joined, from the server's
 * `plan`.
 *
 * A service's name opens its editor, and with ticking on, each row and each
 * heading carries a tick for the bulk bar. With `fold`, a group can be one
 * line with its services' visits merged on the months; the page folds a
 * long agreement until it is narrowed.
 */
export function AgreementGrid({
  page,
  groups,
  by,
  actions,
  fold,
}: {
  page: AgreementPage
  groups: readonly AgreementGroup[]
  by: GroupBy
  actions: ReviewActions
  fold?: Folding
}) {
  const { selected, onSelect, onEditService, onAddSite, onVisit } = actions
  const plan = planByVisit(page)
  const names = new Map(page.schedules.map((row) => [row.id, serviceName(row)]))
  const joinedOf = (row: Row) => {
    const joined = new Map<number, Joined>()
    for (const slot of row.slots) {
      const line = plan.get(visitKey({ scheduleId: row.id, sequence: slot.sequence }))
      if (!line || line.with.length === 0) continue
      const others = visitsOf(line)
        .filter((visit) => visit.scheduleId !== row.id)
        .map((visit) => names.get(visit.scheduleId) ?? 'Another service')
      joined.set(slot.sequence, { key: line.key, others })
    }
    return joined
  }
  const tracks: TrackGroup[] = groups.map((group) => ({
    key: group.key,
    heading: headingFor(group, by, actions, fold),
    folded: fold ? !fold.isOpen(group.key) : false,
    label: group.name,
    aside: groupAside(group.rows),
    rows: group.rows.map((row) => {
      const names = rowNames(row, page.client.id)
      const on = nextOn(row)
      return {
        id: row.id,
        name: by === 'site' ? names.service : names.site,
        sub: [
          everyLabel(row).replace(/^./, (letter) => letter.toUpperCase()),
          row.priceIncTax ? money(row.priceIncTax) : 'no price',
        ].join(' · '),
        onEdit: onEditService ? () => onEditService(row) : undefined,
        editLabel: `Edit ${names.both}`,
        tick:
          selected && onSelect ? (
            <TickBox checked={selected.has(row.id)} onChange={(checked) => onSelect([row.id], checked)} label={`Select ${names.both}`} />
          ) : undefined,
        flags: <RowFlags row={row} />,
        slots: row.slots,
        joined: joinedOf(row),
        onSlot: onVisit && !row.paused ? (slot) => onVisit({ scheduleId: row.id, sequence: slot.sequence }) : undefined,
        paused: row.paused,
        aside: row.paused ? <span className="font-normal text-ink-400">Paused</span> : on ? dayLabel(on, { year: false }) : '—',
      }
    }),
  }))

  return (
    <ServiceTracks
      label="Sites and services"
      months={page.months}
      today={page.today}
      groups={tracks}
      look="dots"
      firstLabel={by === 'site' ? 'Site and service' : 'Service and site'}
      showAside
      showKey={false}
      footer={
        onAddSite ? (
          <button
            type="button"
            onClick={onAddSite}
            className="inline-flex items-center gap-1.5 rounded-md px-1.5 py-1 text-[13px] font-medium text-brand-700 hover:bg-brand-50"
          >
            <Icon name="plus" className="size-4" />
            Add a site
          </button>
        ) : null
      }
    />
  )
}
